import { Injectable } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

interface ModalInstance {
  ref: NgbModalRef;
  zIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class ModalZIndexService {
  private readonly BASE_Z_INDEX = 1055;
  private openModals: ModalInstance[] = [];

  /**
   * Registers a modal and assigns it a z-index
   * @param modalRef - The ng-bootstrap modal reference
   * @returns The assigned z-index
   */
  registerModal(modalRef: NgbModalRef): number {
    const zIndex = this.BASE_Z_INDEX + this.openModals.length;
    
    const modalInstance: ModalInstance = {
      ref: modalRef,
      zIndex
    };
    
    this.openModals.push(modalInstance);
    
    // Apply z-index to modal elements
    this.applyZIndex(modalRef, zIndex);
    
    // Clean up when modal closes
    modalRef.result.finally(() => {
      this.unregisterModal(modalRef);
    });
    
    return zIndex;
  }

  /**
   * Unregisters a modal when it closes
   * @param modalRef - The ng-bootstrap modal reference
   */
  private unregisterModal(modalRef: NgbModalRef): void {
    const index = this.openModals.findIndex(modal => modal.ref === modalRef);
    if (index > -1) {
      this.openModals.splice(index, 1);
    }
  }

  /**
   * Applies the z-index to modal backdrop and window elements
   * Both elements of the same modal should have the same z-index
   * @param modalRef - The ng-bootstrap modal reference
   * @param zIndex - The z-index to apply
   */
  private applyZIndex(modalRef: NgbModalRef, zIndex: number): void {
    // Use setTimeout to ensure modal elements are in DOM
    setTimeout(() => {
      // Get all current modal elements
      const allBackdrops = Array.from(document.querySelectorAll('.modal-backdrop.fade.show')) as HTMLElement[];
      const allModalWindows = Array.from(document.querySelectorAll('.modal.fade.show')) as HTMLElement[];
      
      // Apply z-index to the last backdrop (most recent)
      if (allBackdrops.length > 0) {
        const lastBackdrop = allBackdrops[allBackdrops.length - 1];
        lastBackdrop.style.zIndex = zIndex.toString();
      }

      // Apply z-index to the last modal window (most recent)
      if (allModalWindows.length > 0) {
        const lastModalWindow = allModalWindows[allModalWindows.length - 1];
        lastModalWindow.style.zIndex = zIndex.toString();
      }
    }, 50); // Increased timeout to ensure DOM is ready
  }

  /**
   * Gets the current count of open modals
   * @returns Number of open modals
   */
  getOpenModalCount(): number {
    return this.openModals.length;
  }

  /**
   * Gets the next z-index that would be assigned
   * @returns The next z-index value
   */
  getNextZIndex(): number {
    return this.BASE_Z_INDEX + this.openModals.length;
  }
}